import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useIsDesktop, useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS } from 'Hooks/useDocumentUpload';
import { useDocumentUploadContext } from '../../../../../Context/DocumentUploadContext';
import { DocumentTypeCollapse } from './DocumentTypeCollapse';
import { DocumentTypeButton } from './DocumentTypeButton';

export const DocumentUpload: React.FC = () => {
  const {
    availableDocumentTypes,
    availableTypes,
    documentTypes,
    hasSelectedTypes,
    showDeleteButton,
    addDocumentType,
    toggleCollapse,
    handleDelete,
    handleFileSelect,
    handleDeleteFile,
    handlePreviewFile,
    fileValidationErrorByTypeId,
  } = useDocumentUploadContext();

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);

  const hasNonOtherAttachmentTypes = availableDocumentTypes.some(
    (t) => !OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS.includes(String(t.type))
  );

  return (
    <Box
      sx={{
        maxWidth: isDesktop ? '800px' : '100%',
        margin: '0 auto',
        padding: isDesktop ? '0' : '16px',
      }}
    >
      <Stack gap={'16px'}>
        <Stack gap='4px'>
          <Typography
            variant='body1'
            sx={{
              fontWeight: '700 !important',
            }}
          >
            {dictionary.uploadFile.attachedFilesTitle}
          </Typography>
          <Typography variant='body6'>
            {dictionary.uploadFile.attachedFilesSubtitle}
          </Typography>
        </Stack>

        {documentTypes.map((docType) => (
          <DocumentTypeCollapse
            key={docType.id}
            subjectName={docType.label}
            documentTypeId={docType.type}
            hasNonOtherAttachmentTypes={hasNonOtherAttachmentTypes}
            isOpen={docType.isOpen}
            toggleIsOpen={() => toggleCollapse(docType.id)}
            onDelete={() => handleDelete(docType.id)}
            uploadedFiles={docType.files}
            onFileSelect={(files) => handleFileSelect(docType.id, files)}
            onDeleteFile={(fileId) => handleDeleteFile(docType.id, fileId)}
            onViewFile={(fileId) => handlePreviewFile(docType.id, fileId)}
            showDeleteButton={showDeleteButton}
            availableDocumentTypesLength={availableDocumentTypes.length}
            fileValidationError={fileValidationErrorByTypeId[docType.id]}
          />
        ))}

        {availableDocumentTypes.length > 1 && availableTypes.length > 0 && (
          <Stack
            mt='8px'
            gap='24px'
            p='24px 0px 32px'
            border='1px solid'
            borderColor='#E7EDFF'
            borderRadius='16px'
            boxShadow='0px 2px 12px 0px #0000000A'
            bgcolor='white'
            alignItems='center'
            justifyContent='center'
          >
            <Typography variant='body1'>
              {!!hasSelectedTypes
                ? dictionary.uploadFile.wantToAttachAnother
                : dictionary.uploadFile.whichDocuments}
            </Typography>
            <Stack
              direction='row'
              gap='12px'
              flexWrap='wrap'
              justifyContent='center'
            >
              {availableTypes.map((type) => (
                <DocumentTypeButton
                  key={type.type}
                  label={type.label}
                  onClick={() => addDocumentType(type)}
                  isFirstType={documentTypes.length === 0}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
